/**
 * Key Generation Step - First step of the setup wizard
 *
 * Shows key generation progress and result.
 */

import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { StoredKeyPair } from '@antragsmanager/crypto-lib';

interface KeyGenerationStepProps {
  /** Whether to auto-generate on mount */
  autoGenerate?: boolean;

  /** Callback when key generation is complete */
  onKeyGenerated: (keyPair: StoredKeyPair) => void;

  /** Function to generate key (from parent hook) */
  generateKey: () => Promise<StoredKeyPair>;

  /** Whether generation is in progress */
  isGenerating: boolean;

  /** Error message if any */
  error?: string | null;

  /** The generated key pair (if available) */
  keyPair?: StoredKeyPair | null;
}

export function KeyGenerationStep({
  autoGenerate = true,
  onKeyGenerated,
  generateKey,
  isGenerating,
  error,
  keyPair,
}: KeyGenerationStepProps) {
  const [hasGenerated, setHasGenerated] = useState(false);

  // Auto-generate key on mount
  useEffect(() => {
    if (autoGenerate && !hasGenerated && !keyPair && !isGenerating && !error) {
      setHasGenerated(true);
      generateKey()
        .then(onKeyGenerated)
        .catch(() => {
          // Error will be shown via error prop
        });
    }
  }, [autoGenerate, hasGenerated, keyPair, isGenerating, error, generateKey, onKeyGenerated]);

  // Format fingerprint for display
  const formatFingerprint = (fingerprint: string) => {
    const groups: string[] = [];
    for (let i = 0; i < fingerprint.length; i += 8) {
      groups.push(fingerprint.slice(i, i + 8).toUpperCase());
    }
    return groups.slice(0, 4).join(' '); // Show first 32 chars
  };

  return (
    <Box sx={{ py: 2 }}>
      {/* Generation in progress */}
      {isGenerating && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h6" gutterBottom>
            Schlüssel wird generiert...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Dies kann einige Sekunden dauern.
          </Typography>
        </Box>
      )}

      {/* Error state */}
      {error && !isGenerating && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Success state */}
      {keyPair && !isGenerating && (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Schlüssel erfolgreich erstellt
          </Typography>

          <Box
            sx={{
              mt: 3,
              p: 2,
              bgcolor: 'action.hover',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <VpnKeyIcon color="primary" />
              <Typography variant="subtitle2">Schlüssel-Details</Typography>
            </Box>

            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="body2" color="text.secondary">
                Schlüssel-ID:
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontFamily: 'monospace', mb: 1, wordBreak: 'break-all' }}
              >
                {keyPair.keyId}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Fingerprint:
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {formatFingerprint(keyPair.fingerprint)}
              </Typography>

              <Box sx={{ mt: 2 }}>
                <Chip
                  label={keyPair.algorithm}
                  size="small"
                  variant="outlined"
                  sx={{ mr: 1 }}
                />
                <Chip
                  label={new Date(keyPair.createdAt).toLocaleDateString('de-DE')}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Box>
          </Box>

          <Alert severity="warning" sx={{ mt: 3, textAlign: 'left' }}>
            <Typography variant="body2">
              <strong>Wichtig:</strong> Im nächsten Schritt müssen Sie ein Backup Ihres
              Schlüssels erstellen. Ohne dieses Backup ist ein erneutes Anmelden
              nicht möglich!
            </Typography>
          </Alert>
        </Box>
      )}
    </Box>
  );
}
